// 🔧 sponsor_view.js
// ✅ Purpose: Public page to show full details for a single sponsor
// ✅ Features:
// - Uses standard public layout styling
// - Displays logo (medium), name, description, and external URL
// - Loads sponsor from /events/{eventId}/sponsors/{sponsorSlug}

import { isEventPublic } from "../../utils/event_visibility";
import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  getDocs,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  limit,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import SeoHead from "../../components/seo_head";
import { useAuth } from "../../auth_context";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";
import { sanitizeHtml } from "../../utils/sanitize_html";
import { getPowerupPath, resolvePowerupSnapshot } from "../../utils/powerup_urls";

const SponsorView = () => {

  const {
    eventslug,
    eventId,
    eventLoading,
    ready,
    eventDoc,
  } = useEventContext({
    redirectIfNotFound: true,
  });
  const { sponsorSlug } = useParams();
  const [searchParams] = useSearchParams();
  const sponsorPaymentStatus = searchParams.get("sponsor_payment") || "";
  const [sponsor, setSponsor] = useState(null);
  const [loadingSponsor, setLoadingSponsor] = useState(true);
  const { user } = useAuth();

  // =====================================================
  // 🤝 Sponsor Lead CTA State
  // Purpose:
  // - Allows attendees/customers to opt in to sharing contact info
  // - Stores consented sponsor leads under this event
  // - Prevents accidental duplicate submissions
  // =====================================================
  const [leadForm, setLeadForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    consent: false,
  });

  const [leadStatus, setLeadStatus] = useState(null);
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  useEffect(() => {
    if (!eventId || !sponsorSlug) return;

    const fetchSponsor = async () => {
      try {
        const sponsorDoc = await resolvePowerupSnapshot(db, eventId, "sponsor", sponsorSlug);

        if (sponsorDoc) {
          const sponsorData = {
            id: sponsorDoc.id,
            ...sponsorDoc.data(),
          };

          // ✅ Public sponsor visibility protection
          // ✅ Purpose:
          // - Do not show unpaid/pending sponsors on the public detail page
          // - Older sponsor records without payment fields remain visible
          const hasModernApprovalFields =
            sponsorData.paymentStatus ||
            sponsorData.approvalStatus ||
            sponsorData.isApproved !== undefined;

          const sponsorIsPublic =
            !hasModernApprovalFields ||
            sponsorData.isApproved === true ||
            sponsorData.approvalStatus === "approved" ||
            sponsorData.paymentStatus === "paid" ||
            sponsorData.paymentStatus === "not_required";

          if (!sponsorIsPublic) {
            setSponsor(null);
            return;
          }

          setSponsor(sponsorData);

          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }
      } catch (err) {
        logger.error("❌ Error loading sponsor:", {
          value: err,
        });
      } finally {
        setLoadingSponsor(false);
      }
    };

    fetchSponsor();
  }, [eventId, sponsorSlug]);

    useEffect(() => {
    if (!ready || !eventId || !sponsor?.id) return;

    trackEvent({
      action: GA_EVENTS.SPONSOR_VIEWED,
      parameters: {
        event_slug: eventslug,
        event_id: eventId,
        sponsor_id: sponsor.id,
        sponsor_name: sponsor.name || "",
        sponsor_level: sponsor.level || "",
        source_page: window.location.pathname,
      },
    });
  }, [ready, eventId, eventslug, sponsor?.id]);

  useEffect(() => {
    if (!leadStatus?.message) return;

    const timer = setTimeout(() => {
      setLeadStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [leadStatus]);

  useEffect(() => {
    if (!user) return;

    setLeadForm((prev) => ({
      ...prev,
      email: prev.email || user.email || "",
    }));
  }, [user]);

  // =====================================================
  // 🎗 Sponsors Add-on Page Gate
  // =====================================================
  // ✅ Purpose:
  // - Prevent direct URL access to sponsor pages when the Sponsors add-on is inactive
  // - Keeps public, sponsor-contact, and manager sponsor tools hidden
  // - Friendly message instead of exposing sponsor functionality
  // =====================================================
  const sponsorsEnabled =
    hasPurchasedAddOn(eventDoc, "sponsors") ||
    hasPurchasedAddOn(eventDoc, "sponsor");

  if (!sponsorsEnabled) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "900px" }}>
          <div className="alert alert-info text-center mb-0">
            Sponsors are not active for this event.
          </div>
        </div>
      </section>
    );
  }

  const handleLeadChange = (e) => {
    const { name, value, type, checked } = e.target;

    setLeadForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSponsorLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadStatus(null);

    const firstName = leadForm.firstName.trim();
    const lastName = leadForm.lastName.trim();
    const email = leadForm.email.trim().toLowerCase();
    const phone = leadForm.phone.trim();

    if (!firstName || !lastName || !email) {
      setLeadStatus({
        type: "error",
        message: "Please enter your name and email before submitting.",
      });
      return;
    }

    if (!leadForm.consent) {
      setLeadStatus({
        type: "error",
        message: "Please check the consent box before sharing your information.",
      });
      return;
    }

    try {
      setLeadSubmitting(true);

      const leadsRef = collection(db, "events", eventId, "sponsor_leads");

      // ✅ Duplicate protection
      // Purpose:
      // - Prevents the same email from submitting multiple leads
      //   for the same sponsor.
      const duplicateQuery = query(
        leadsRef,
        where("sponsorId", "==", sponsor.id),
        where("email", "==", email),
        limit(1)
      );

      const duplicateSnap = await getDocs(duplicateQuery);

      if (!duplicateSnap.empty) {
        setLeadSubmitted(true);
        setLeadStatus({
          type: "warning",
          message: `You have already shared your information with ${sponsor.name}.`,
        });
        return;
      }

      await addDoc(leadsRef, {
        sponsorId: sponsor.id,
        sponsorName: sponsor.name || "",
        sponsorLevel: sponsor.level || "",
        sponsorSlug,

        uid: user?.uid || "",
        firstName,
        lastName,
        email,
        phone,

        source: "sponsor_page",
        eventId,
        consentGiven: true,
        createdAt: serverTimestamp(),
      });

      trackEvent({
        action: GA_EVENTS.SPONSOR_LEAD_SUBMITTED,
        parameters: {
          event_slug: eventslug,
          event_id: eventId,
          sponsor_id: sponsor.id,
          sponsor_name: sponsor.name || "",
          sponsor_level: sponsor.level || "",
          source_page: window.location.pathname,
        },
      });

      setLeadSubmitted(true);

      setLeadStatus({
        type: "success",
        message: `Thank you! Your information has been shared with ${sponsor.name}.`,
      });
    } catch (err) {
      logger.error("❌ Failed to save sponsor lead:", {
        value: err,
      });

      setLeadStatus({
        type: "error",
        message: "Unable to share your information right now. Please try again.",
      });
    } finally {
      setLeadSubmitting(false);
    }
  };

  // =====================================================
  // 🔎 Dynamic SEO
  // =====================================================

  const breadcrumbs = [
    { name: "Home", path: "/" },
    {
      name: eventDoc?.name || "Event",
      path: `/${eventslug}/event`,
    },
    {
      name: "Sponsors",
      path: `/${eventslug}/sponsors`,
    },
    {
      name: sponsor?.name || "Sponsor",
      path: getPowerupPath(eventslug, "sponsor", sponsor),
    },
  ];

  const sponsorSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: sponsor?.name,
    url:
      sponsor?.url ||
      sponsor?.website ||
      undefined,
      description: sponsor?.description || undefined,
    logo: sponsor?.logoUrl || undefined,

    sameAs: sponsor?.url
      ? [sponsor.url]
      : undefined,    
  };

  const sponsorSeo = {
    title:
      `${sponsor?.name || "Sponsor"} | ${
        eventDoc?.name || "Let's Go Vote"
      }`,

    description:
      sponsor?.description ||
      `${sponsor?.name || "Sponsor"} is supporting ${
        eventDoc?.name || "this event"
      }.`,

    path:
      getPowerupPath(eventslug, "sponsor", sponsor),

    image:
      sponsor?.logoUrl ||
      eventDoc?.eventLogo ||
      "https://letsgovote.app/images/social-share-default.png",

    noindex: !isEventPublic(eventDoc),
    breadcrumbs,
    structuredData: sponsorSchema,
  };

  if (!ready || eventLoading || loadingSponsor) {
    return <FullPageSpinner message="Loading Sponsor..." />;
  }

  if (!sponsor) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="container">
          <div className="alert alert-danger text-center">❌ Sponsor not found.</div>
        </div>
      </section>
    );
  }

  return (
  <>
    <SeoHead {...sponsorSeo} />
      <section id="top-content" className="container-fluid py-5">
      <div className="container">
        <div className="row mb-4">
          <div className="col text-start">
            <h1 className="display-5">🎗️ Sponsor Spotlight</h1>
            <p className="lead text-muted">Learn more about this event supporter</p>
          </div>
        </div>

        {sponsorPaymentStatus === "success" && (
          <div className="alert alert-success mb-4">
            ✅ Thank you! Your sponsorship payment was completed successfully.
            Your sponsorship will be approved automatically once payment confirmation finishes processing.
          </div>
        )}

        {sponsorPaymentStatus === "cancelled" && (
          <div className="alert alert-warning mb-4">
            Sponsorship payment was cancelled. You can use the payment link from your email if you would like to try again.
          </div>
        )}

        <div className="white-section p-4 rounded shadow-sm bg-white">
            <div className="row align-items-center">
                {/* Left: Logo */}
                <div className="col-md-4 text-center mb-4 mb-md-0">
                {sponsor.logoUrl && (
                    <img
                    src={sponsor.logoUrl}
                    alt={sponsor.name}
                    className="img-fluid"
                    style={{ maxHeight: "200px", objectFit: "contain" }}
                    />
                )}
                </div>

                {/* Right: Name + Description + Link */}
                <div className="col-md-8">
                <h2 className="fw-bold mb-3">{sponsor.name}</h2>

                {sponsor.level && (
                  <div className="mb-3 d-flex flex-wrap gap-2">
                    <span className="badge bg-secondary fs-6">
                      Sponsor Level: {sponsor.level}
                    </span>

                    {(sponsor.paymentStatus === "paid" ||
                      sponsor.paymentStatus === "not_required" ||
                      sponsor.isApproved === true) && (
                      <span className="badge bg-success fs-6">
                        Approved Sponsor
                      </span>
                    )}
                  </div>
                )}

                {sponsor.description && (
                    <div
                    className="mb-3"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(sponsor.description) }}
                    />
                )}

                {sponsor.url && (
                    <a
                    href={sponsor.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    onClick={() => {
                      trackEvent({
                        action: GA_EVENTS.SPONSOR_WEBSITE_CLICKED,
                        parameters: {
                          event_slug: eventslug,
                          event_id: eventId,
                          sponsor_id: sponsor.id,
                          sponsor_name: sponsor.name || "",
                          source_page: window.location.pathname,
                        },
                      });
                    }}
                    >
                      🌐 Visit the {sponsor.name} Website
                    </a>
                )}

                {/* =====================================================
                    🤝 Sponsor Lead CTA
                    Purpose:
                    - Lets attendees/customers opt in to sharing contact info
                    - Creates measurable sponsor value
                    - Requires explicit consent before saving
                ===================================================== */}
                <div className="card border shadow-sm mt-4">
                  <div className="card-body">
                    <h4 className="fw-bold mb-3">
                      🤝 Interested in This Sponsor?
                    </h4>

                    <p className="text-muted">
                      With your permission, we can share your contact information
                      with <strong>{sponsor.name}</strong> so they can contact you
                      about products, services, special offers, or future events.
                    </p>

                    {leadStatus?.message && (
                      <div
                        className={`alert ${
                          leadStatus.type === "success"
                            ? "alert-success"
                            : leadStatus.type === "warning"
                            ? "alert-warning"
                            : "alert-danger"
                        }`}
                      >
                        {leadStatus.message}
                      </div>
                    )}

                    <form onSubmit={handleSponsorLeadSubmit}>
                      <div className="row g-3">
                        <div className="col-12 col-md-6">
                          <label className="form-label">First Name*</label>
                          <input
                            name="firstName"
                            className="form-control"
                            value={leadForm.firstName}
                            onChange={handleLeadChange}
                            disabled={leadSubmitted}
                          />
                        </div>

                        <div className="col-12 col-md-6">
                          <label className="form-label">Last Name*</label>
                          <input
                            name="lastName"
                            className="form-control"
                            value={leadForm.lastName}
                            onChange={handleLeadChange}
                            disabled={leadSubmitted}
                          />
                        </div>

                        <div className="col-12 col-md-6">
                          <label className="form-label">Email*</label>
                          <input
                            type="email"
                            name="email"
                            className="form-control"
                            value={leadForm.email}
                            onChange={handleLeadChange}
                            disabled={leadSubmitted}
                          />
                        </div>

                        <div className="col-12 col-md-6">
                          <label className="form-label">Phone</label>
                          <input
                            name="phone"
                            className="form-control"
                            value={leadForm.phone}
                            onChange={handleLeadChange}
                            disabled={leadSubmitted}
                          />
                        </div>

                        <div className="col-12">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              name="consent"
                              id="sponsorLeadConsent"
                              className="form-check-input"
                              checked={leadForm.consent}
                              onChange={handleLeadChange}
                              disabled={leadSubmitted}
                            />

                            <label
                              className="form-check-label"
                              htmlFor="sponsorLeadConsent"
                            >
                              I consent to sharing my contact information with{" "}
                              <strong>{sponsor.name}</strong>.
                            </label>
                          </div>

                          <div className="form-text">
                            Your information is only shared if you choose to send it.
                          </div>
                        </div>

                        <div className="col-12">
                          <button
                            type="submit"
                            className="btn btn-success"
                            disabled={
                              leadSubmitting ||
                              leadSubmitted ||
                              !leadForm.consent
                            }
                          >
                            {leadSubmitting
                              ? "Sharing..."
                              : leadSubmitted
                              ? "✅ Information Shared"
                              : "📩 Share My Information"}
                          </button>

                          {!leadForm.consent && !leadSubmitted && (
                            <div className="form-text text-muted mt-2">
                              Please check the consent box to enable the Share My Information button.
                            </div>
                          )}     
                                               
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                </div>
            </div>
        </div>
      </div>
    </section>

    <section className="container-fluid py-5 bg-light">
      <div className="text-center mt-4">
        <Link
          to={`/${eventslug}/sponsors`}
          className="btn btn-outline-primary"
        >
          ← Back to All Sponsors
        </Link>
      </div>
    </section>
    </>
  );
};

export default SponsorView;
